import React from 'react';
import { FaBullseye, FaEye, FaRocket, FaCalendarCheck } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const About = () => {
  return (
    <div id="about" className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-secondary to-primary text-white">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-6 animate-fade-in-down">Sobre Nosotros</h1>
          <p className="text-xl text-center max-w-3xl mx-auto animate-fade-in-up">
            Comprometidos con tu bienestar y salud integral a través de un servicio médico de excelencia.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0 md:pr-8 animate-fade-in-up">
              <div className="bg-primary/10 dark:bg-primary/20 p-6 rounded-lg border border-primary/20 dark:border-primary/30">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mb-4 mx-auto md:mx-0">
                  <FaBullseye size={24} />
                </div>
                <h2 className="text-2xl font-bold mb-4 text-secondary dark:text-white">Nuestra Misión</h2>
                <p className="text-gray-600 dark:text-gray-300">
                  Proporcionar atención médica de calidad, accesible y personalizada, mejorando la calidad de vida de nuestros pacientes a través de un equipo de profesionales altamente calificados y tecnología de vanguardia.
                </p>
              </div>
            </div>
            <div className="md:w-1/2 animate-fade-in-up animate-delay-100">
              <img 
                aria-label="Equipo médico"
                name="Equipo médico"
                id="Equipo médico"
                width="500"
                height="500"
                src="https://i.ibb.co/zHJLgSjT/pexels-rdne-6129507.jpg" 
                alt="Equipo médico" 
                className="rounded-lg shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800/50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row-reverse items-center">
            <div className="md:w-1/2 mb-8 md:mb-0 md:pl-8 animate-fade-in-up">
              <div className="bg-secondary/10 dark:bg-secondary/20 p-6 rounded-lg border border-secondary/20 dark:border-secondary/30">
                <div className="w-16 h-16 bg-secondary text-white rounded-full flex items-center justify-center mb-4 mx-auto md:mx-0">
                  <FaEye size={24} />
                </div>
                <h2 className="text-2xl font-bold mb-4 text-secondary dark:text-white">Nuestra Visión</h2>
                <p className="text-gray-600 dark:text-gray-300">
                  Ser reconocidos como el centro médico líder en la región, destacando por nuestra excelencia clínica, innovación tecnológica y calidez humana, siendo referentes en la transformación de la atención médica.
                </p>
              </div>
            </div>
            <div className="md:w-1/2 animate-fade-in-up animate-delay-100">
              <img 
                aria-label="Instalaciones"
                name="Instalaciones"
                id="Instalaciones"
                width="500"
                height="500"
                src="https://i.ibb.co/SXsVjq9D/pexels-tomfisk-1770818.jpg" 
                alt="Instalaciones" 
                className="rounded-lg shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Objectives Section */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-secondary dark:text-white">Nuestros Objetivos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <FaRocket className="text-4xl text-primary" />,
                title: "Innovación Continua",
                description: "Implementar las últimas tecnologías y procedimientos médicos para ofrecer tratamientos de vanguardia."
              },
              {
                icon: <FaCalendarCheck className="text-4xl text-secondary" />,
                title: "Atención Personalizada",
                description: "Brindar un trato cercano y personalizado a cada paciente, entendiendo sus necesidades específicas."
              },
              {
                icon: <FaBullseye className="text-4xl text-primary" />,
                title: "Excelencia Clínica",
                description: "Mantener los más altos estándares de calidad en todos nuestros servicios médicos."
              }
            ].map((item, index) => (
              <div 
                key={index} 
                className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 dark:border-gray-700 hover:border-primary/30 dark:hover:border-primary/50"
              >
                <div className="mb-4">{item.icon}</div>
                <h3 className="text-xl font-semibold mb-2 text-secondary dark:text-white">{item.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-secondary to-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">¿Listo para cuidar de tu salud?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Agenda una cita con nuestros especialistas y comienza tu camino hacia una mejor salud hoy mismo.
          </p>
          <Link 
            to="/contact"
            aria-label="Agendar Cita"
            id="Agendar Cita"
            name="Agendar Cita"
            className="bg-white text-secondary hover:bg-gray-100 font-semibold py-3 px-8 rounded-xl text-lg transition-all duration-300 transform hover:scale-105">
            Agendar Cita
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;